import { Button, Callout, DATE_FORMAT } from '@dev-spendesk/grapes';
import { parseISO } from 'date-fns';
import React from 'react';
import { Trans } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';

import { useTranslation } from 'common/hooks/useTranslation';
import { useCompany } from 'modules/app/hooks/useCompany';
import { useUser } from 'modules/app/hooks/useUser';
import type { CardBlockingReason } from 'modules/physical-cards/card';
import { routeFor, routes } from 'src/core/constants/routes';
import { AnalyticEventName, track } from 'src/core/utils/analytics';

type Props = {
  card?: {
    user_id: string;
  };
  cardBlockingReason?: CardBlockingReason;
  isFullPage?: boolean;
  className?: string;
};

export const RestrictedAccessWarning = ({
  card,
  cardBlockingReason,
  isFullPage = false,
  className,
}: Props) => {
  const { t, localeFormat } = useTranslation('global');
  const company = useCompany();
  const history = useHistory();
  const location = useLocation();
  const user = useUser();

  if (!cardBlockingReason) {
    return null;
  }

  const initiator = cardBlockingReason.initiator;

  const restrictedUserName =
    (cardBlockingReason.initiator === 'user' ||
      cardBlockingReason.initiator === 'subscriptionProcess') &&
    cardBlockingReason.firstName
      ? `${cardBlockingReason.firstName} ${cardBlockingReason.lastName}`
      : null;

  const getBlockedReasonTitle = () => {
    if (
      (cardBlockingReason.initiator === 'user' ||
        cardBlockingReason.initiator === 'subscriptionProcess') &&
      cardBlockingReason.userId === user.id &&
      (!card || card.user_id === user.id)
    ) {
      return t('restrictedAccessWarning.titles.restrictedSelf', {
        date: localeFormat(
          parseISO(cardBlockingReason.createdAt),
          DATE_FORMAT.MEDIUM,
        ),
      });
    }

    if (
      (cardBlockingReason.initiator === 'user' ||
        cardBlockingReason.initiator === 'subscriptionProcess') &&
      restrictedUserName
    ) {
      return t('restrictedAccessWarning.titles.restrictedAdminUser', {
        user: restrictedUserName,
        date: localeFormat(
          parseISO(cardBlockingReason.createdAt),
          DATE_FORMAT.MEDIUM,
        ),
      });
    }

    if (cardBlockingReason.initiator === 'user' && !restrictedUserName) {
      return t('restrictedAccessWarning.titles.restrictedAdminSelf', {
        date: localeFormat(
          parseISO(cardBlockingReason.createdAt),
          DATE_FORMAT.MEDIUM,
        ),
      });
    }

    if (cardBlockingReason.initiator === 'controlRuleProcess') {
      return t('restrictedAccessWarning.titles.restrictedByControlRules');
    }

    if (cardBlockingReason.initiator === 'scheduleProcess') {
      return t('restrictedAccessWarning.titles.restrictedBySchedule');
    }

    if (cardBlockingReason.initiator === 'agent') {
      return t('restrictedAccessWarning.titles.restrictedByAgent', {
        date: localeFormat(
          parseISO(cardBlockingReason.createdAt),
          DATE_FORMAT.MEDIUM,
        ),
      });
    }

    return t('restrictedAccessWarning.title');
  };

  const getCalloutContent = () => {
    if (
      (cardBlockingReason.initiator === 'user' &&
        cardBlockingReason.userId !== user.id) ||
      initiator === 'agent'
    ) {
      return (
        <div>{t('restrictedAccessWarning.actions.contactAdministrator')}</div>
      );
    }

    if (initiator === 'controlRuleProcess') {
      return (
        <>
          <div>
            <Trans i18nKey="restrictedAccessWarning.actions.descriptionRestrictedByControlRulesSelf" />
          </div>
          <Button
            fit={isFullPage ? 'content' : 'parent'}
            variant="primaryAlert"
            text={t('restrictedAccessWarning.actions.buttonAttachReceipts')}
            className="mt-16"
            onClick={() => {
              track(
                AnalyticEventName.RESTRICTED_JUSTIFY_PAYMENTS_BUTTON_CLICKED,
                { from: location.pathname },
              );
              const paymentRoute = routeFor(routes.PAYMENTS_ALL.path, {
                company: company.id,
              });
              history.push(`${paymentRoute}?mode=requester`);
            }}
          />
        </>
      );
    }

    if (initiator === 'scheduleProcess') {
      return (
        <div>
          <Trans i18nKey="restrictedAccessWarning.actions.restrictedBySchedule" />
        </div>
      );
    }

    return null;
  };

  return (
    <Callout
      iconName={
        initiator === 'scheduleProcess' ? 'triangle-warning' : 'lock-close'
      }
      variant={initiator === 'scheduleProcess' ? 'warning' : 'alert'}
      title={getBlockedReasonTitle()}
      className={className}
    >
      {getCalloutContent()}
    </Callout>
  );
};
